"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import SuperTopBar from "./SuperTopBar";
import { client } from "@/sanity/lib/client";

interface Callout {
  text: string;
  icon: string;
  linkUrl?: string;
}

interface SuperTopBarData {
  staticText?: string;
  callouts?: Callout[];
  enabled?: boolean;
}

export default function SuperTopBarConditionalClient() {
  const pathname = usePathname();
  const [showTopBar, setShowTopBar] = useState(true);
  const [topBarData, setTopBarData] = useState<SuperTopBarData | null>(null);

  useEffect(() => {
    const fetchDataAndCheck = async () => {
      // Fetch SuperTopBar data
      try {
        const themeQuery = `*[_type == "websiteTheme"][0]{
          superTopBar{
            enabled,
            staticText,
            callouts[]{
              text,
              icon,
              linkUrl
            }
          }
        }`;
        const theme = await client.fetch(themeQuery);
        const topBar = theme?.superTopBar;
        
        if (!topBar?.enabled || !topBar?.callouts || topBar.callouts.length === 0) {
          setTopBarData(null);
          setShowTopBar(false);
          return;
        }

        // Filter and map callouts to ensure required fields are present
        const validCallouts: Callout[] = (topBar.callouts || [])
          .filter((callout: any) => callout?.text && callout?.icon)
          .map((callout: any) => ({
            text: callout.text as string,
            icon: callout.icon as string,
            linkUrl: callout.linkUrl,
          }));

        setTopBarData({
          staticText: topBar.staticText,
          callouts: validCallouts.length > 0 ? validCallouts : undefined,
          enabled: topBar.enabled,
        });
      } catch (error) {
        console.error("Error fetching SuperTopBar data:", error);
        setTopBarData(null);
        setShowTopBar(false);
        return;
      }

      // Check if we're on a press release page
      if (!pathname) {
        setShowTopBar(true);
        return;
      }

      // Parse pathname: /category or /category/slug
      const pathParts = pathname.split("/").filter(Boolean);

      // Check if we're on a slug page (has 2 segments: category and slug)
      if (pathParts.length >= 2) {
        const category = pathParts[0];
        const slug = pathParts[1];

        try {
          // Query to check if this is a press release
          const postQuery = `*[(_type == "post" || _type == "postPressRelease") && slug.current == $slug && $category in categories[]->slug.current][0]{
            _type
          }`;
          const post = await client.fetch(postQuery, { slug, category });
          
          // Hide SuperTopBar for press releases
          if (post?._type === "postPressRelease") {
            setShowTopBar(false);
            return;
          }
        } catch (error) {
          console.error("Error checking press release:", error);
          // If there's an error, show the SuperTopBar anyway
        }
      }

      setShowTopBar(true);
    };

    fetchDataAndCheck();
  }, [pathname]);

  if (!showTopBar || !topBarData) {
    return null;
  }

  return (
    <SuperTopBar
      staticText={topBarData.staticText}
      callouts={topBarData.callouts}
      enabled={topBarData.enabled}
    />
  );
}
